import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api.ts';
import { Shell, Spinner } from './ui.tsx';
import { WelcomeScreen, LoginScreen, ThankYouScreen, MessageScreen } from './screens.tsx';
import { SurveyRunner } from './SurveyRunner.tsx';

export function SurveyFlow({ slug }: { slug: string }) {
  const [phase, setPhase] = useState<'welcome' | 'login'>('welcome');

  const brandingQ = useQuery({ queryKey: ['branding', slug], queryFn: () => api.branding(slug) });
  const surveyQ = useQuery({
    queryKey: ['survey', slug],
    // Slugged so a session cookie left over from a different engagement (tested earlier
    // in this browser) 401s here instead of silently resuming under the wrong slug.
    queryFn: () => api.survey(slug),
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
  });

  const logout = async () => {
    await api.logout(slug).catch(() => {});
    setPhase('welcome');
    await surveyQ.refetch(); // now 401s → drops back to welcome/login
  };

  if (brandingQ.isLoading) return <Shell><Spinner /></Shell>;
  if (brandingQ.isError || !brandingQ.data) {
    return (
      <Shell>
        <MessageScreen title="Survey not found" body="Please check the link provided by your administrator." />
      </Shell>
    );
  }

  const branding = brandingQ.data;
  const client = branding.clientName;

  if (branding.windowState === 'closed') {
    return (
      <Shell client={client}>
        <MessageScreen title="This survey has closed" body="Thank you for your interest." />
      </Shell>
    );
  }

  // authenticated (has a valid session cookie)
  if (surveyQ.isSuccess) {
    const survey = surveyQ.data;
    if (survey.respondent.submitted) {
      return (
        <Shell client={client} onLogout={logout}>
          <ThankYouScreen />
        </Shell>
      );
    }
    return (
      <Shell client={client} onLogout={logout}>
        <SurveyRunner slug={slug} survey={survey} onSubmitted={() => void surveyQ.refetch()} />
      </Shell>
    );
  }

  if (branding.windowState === 'not_open') {
    return (
      <Shell client={client}>
        <MessageScreen title="This survey is not open yet" body="Please return during the assessment window." />
      </Shell>
    );
  }

  // not authenticated → welcome then login
  return (
    <Shell client={client}>
      {phase === 'welcome' ? (
        <WelcomeScreen branding={branding} onStart={() => setPhase('login')} />
      ) : (
        <LoginScreen branding={branding} onSuccess={() => void surveyQ.refetch()} />
      )}
    </Shell>
  );
}
